var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
/**
 * Raw-JSON editor for a single `module_setting` key (TASK-180 MVP per
 * ADR-0005 D4). Shared component: TASK-180 mounts it under the global
 * Stripes Settings panel; TASK-182 will re-mount it under the in-module
 * nav `/czna-authorities/settings` without duplicating logic.
 *
 * Layout (per task ASCII mock):
 *   ┌─ <textarea, monospace, ~30 rows> ─────────────────────────────┐
 *   │ Status: ✓ customized (last edit ... by USER)                  │
 *   │ Default version: <sha last7>                                  │
 *   │ [Copy] [Reset] [Save]                                         │
 *   │ ── Validation errors ──                                        │
 *   │ ✗ path | code | message                                       │
 *   └────────────────────────────────────────────────────────────────┘
 *
 * Behaviour map (per implementation plan):
 *   - GET 200      → mount editor with JSON.stringify(value, null, 2)
 *   - GET 404      → "no config yet" placeholder, no editor
 *   - GET 403      → "admin only" placeholder
 *   - Save 200     → invalidate cross-task cache, toast success
 *   - Save 400     → render errors panel under textarea
 *   - Save 409     → conflict banner with reload action
 *   - Reset 200    → refresh from default
 *   - Reset 409    → "no default available" placeholder under reset btn
 *   - Live validate (debounced 500 ms) → render errors panel + block Save
 *
 * The renderer-side i18n strategy stays inline-cs per ADR-0005 D9 — only
 * the app-chrome strings (button labels, panel title, toast, banner copy)
 * are translation keys. The JSON content the admin edits is NOT
 * translated by this component.
 */
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from 'react-query';
import { Button, ConfirmationModal, Pane, PaneFooter, Callout, MessageBanner, Loading, } from '@folio/stripes/components';
import useModuleSetting, { moduleSettingInvalidationKeys, } from '../hooks/useModuleSetting';
import useSaveModuleSetting from '../hooks/useSaveModuleSetting';
import useResetModuleSetting from '../hooks/useResetModuleSetting';
import useValidateFormConfig from '../hooks/useValidateFormConfig';
import useUserName from '../hooks/useUserName';
import JsonCodeEditor from './JsonCodeEditor';
import RawImportExport from './RawImportExport';
var formatDefaultVersion = function (sha) {
    if (!sha)
        return '—';
    // sha256-abc... → take the last 7 alphanumeric chars as the human form.
    var trimmed = sha.replace(/^sha256-/, '');
    return trimmed.length > 7 ? trimmed.slice(-7) : trimmed;
};
var formatTimestamp = function (iso, locale) {
    if (!iso)
        return '—';
    try {
        var d = new Date(iso);
        return d.toLocaleString(locale, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
        });
    }
    catch (_a) {
        return iso;
    }
};
var ModuleSettingEditor = function (_a) {
    var _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
    var settingKey = _a.settingKey, header = _a.header, seedTextOverride = _a.seedTextOverride, value = _a.value, onChange = _a.onChange, onDirtyChange = _a.onDirtyChange;
    var intl = useIntl();
    var queryClient = useQueryClient();
    // Convention: project-wide pattern is `useRef<Record<string, any>>(null)`
    // + `ref={calloutRef as React.Ref<any>}` (see `RecordForm.tsx:202`,
    // `AuthoritiesList.tsx:118`). The looser type avoids a 3-step
    // `unknown`-via-cast at the JSX site.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    var calloutRef = useRef(null);
    var _o = useModuleSetting(settingKey), data = _o.data, isLoading = _o.isLoading, isError = _o.isError, errorReason = _o.errorReason;
    var save = useSaveModuleSetting(settingKey);
    var reset = useResetModuleSetting(settingKey);
    // Controlled-vs-uncontrolled split (TASK-181 review FIX 1):
    // when both `value` and `onChange` are supplied the parent owns the
    // textarea state and we forward writes through `onChange`. Otherwise
    // the local `useState` keeps the TASK-180 behaviour intact.
    var isControlled = value !== undefined && typeof onChange === 'function';
    var _p = useState(''), internalTextareaValue = _p[0], setInternalTextareaValue = _p[1];
    var textareaValue = isControlled ? value : internalTextareaValue;
    var setTextareaValue = function (next) {
        if (isControlled) {
            onChange(next);
        }
        else {
            setInternalTextareaValue(next);
        }
    };
    var _q = useState(false), confirmResetOpen = _q[0], setConfirmResetOpen = _q[1];
    var _r = useState(null), parseGuardError = _r[0], setParseGuardError = _r[1];
    var lastLoadedKeyRef = useRef(null);
    var lastLoadedUpdatedAtRef = useRef(null);
    // IMPROVE-1: reset-on-key-change MUST run before the data-reseed
    // effect. Today react-query returns `data: undefined` on key change
    // and the reseed effect is a no-op until the new GET resolves, but
    // adding `keepPreviousData` later would silently break the reset
    // (reseed would fire with the *old* key's data before the reset
    // cleared the refs). Declaring the reset effect first guarantees the
    // refs are clean before the reseed runs in the same render cycle.
    useEffect(function () {
        if (lastLoadedKeyRef.current !== null && lastLoadedKeyRef.current !== settingKey) {
            setTextareaValue('');
            setParseGuardError(null);
            lastLoadedKeyRef.current = null;
            lastLoadedUpdatedAtRef.current = null;
            save.reset();
            reset.reset();
        }
        // We intentionally exclude save/reset from deps — they're mutation
        // handles whose identity is stable per react-query render contract.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settingKey]);
    // Reseed textarea on any fresh backend payload (initial load, refetch,
    // post-save invalidate, post-reset invalidate). We key off updatedAt
    // because react-query may hand us the same `data` reference on subscribe.
    //
    // TASK-181: if `seedTextOverride` is supplied on mount (or set later via
    // a key change in the parent FormConfigSettings), it takes precedence
    // over the server payload. The override is one-shot: once we've seeded
    // from it, subsequent server refreshes (post-save invalidate) reseed
    // from server payload as usual.
    var lastSeedOverrideRef = useRef(undefined);
    useEffect(function () {
        var _a;
        if (seedTextOverride !== undefined && seedTextOverride !== null && lastSeedOverrideRef.current !== seedTextOverride) {
            setTextareaValue(seedTextOverride);
            setParseGuardError(null);
            lastSeedOverrideRef.current = seedTextOverride;
            // Mark that we've "consumed" the server payload for this key/updatedAt
            // so the data effect below doesn't immediately clobber the override.
            lastLoadedKeyRef.current = settingKey;
            lastLoadedUpdatedAtRef.current = (_a = data === null || data === void 0 ? void 0 : data.updatedAt) !== null && _a !== void 0 ? _a : null;
            return;
        }
        if (!data)
            return;
        if (lastLoadedKeyRef.current === settingKey
            && lastLoadedUpdatedAtRef.current === data.updatedAt) {
            return;
        }
        setTextareaValue(JSON.stringify(data.value, null, 2));
        setParseGuardError(null);
        lastLoadedKeyRef.current = settingKey;
        lastLoadedUpdatedAtRef.current = data.updatedAt;
        // `setTextareaValue` is a stable local wrapper around either the
        // local setState (uncontrolled) or the parent `onChange` (controlled
        // mode, TASK-181 review FIX 1). Listing it would force re-runs every
        // time the parent passes a new `onChange` reference — which would
        // refire this effect even though `data` and `settingKey` haven't
        // changed, clobbering the textarea on every parent rerender.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, settingKey, seedTextOverride]);
    // CZNA-1563: "unsaved" = the text differs from the row on screen. Between a
    // new row arriving and the reseed effect above it reads true for one render;
    // the effect below then reports false again, so only a real edit sticks.
    var isDirty = data !== undefined
        && textareaValue !== JSON.stringify(data.value, null, 2);
    useEffect(function () {
        onDirtyChange === null || onDirtyChange === void 0 ? void 0 : onDirtyChange(isDirty);
    }, [isDirty, onDirtyChange]);
    // Live validate (debounced 500 ms).
    var validate = useValidateFormConfig(settingKey, textareaValue);
    var updatedByName = useUserName((_b = data === null || data === void 0 ? void 0 : data.updatedBy) !== null && _b !== void 0 ? _b : null);
    var handleCopy = function () { return __awaiter(void 0, void 0, void 0, function () {
        var _a;
        var _b, _c;
        return __generator(this, function (_d) {
            switch (_d.label) {
                case 0:
                    _d.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, navigator.clipboard.writeText(textareaValue)];
                case 1:
                    _d.sent();
                    (_b = calloutRef.current) === null || _b === void 0 ? void 0 : _b.sendCallout({
                        type: 'success',
                        message: intl.formatMessage({ id: 'ui-czna-authorities.settings.forms.toast.copied' }),
                    });
                    return [3 /*break*/, 3];
                case 2:
                    _a = _d.sent();
                    (_c = calloutRef.current) === null || _c === void 0 ? void 0 : _c.sendCallout({
                        type: 'error',
                        message: intl.formatMessage({ id: 'ui-czna-authorities.settings.forms.toast.copyFailed' }),
                    });
                    return [3 /*break*/, 3];
                case 3: return [2 /*return*/];
            }
        });
    }); };
    var handleSave = function () {
        if (!data)
            return;
        var parsed;
        try {
            parsed = JSON.parse(textareaValue);
        }
        catch (err) {
            setParseGuardError(err instanceof Error ? err.message : 'Invalid JSON');
            return;
        }
        setParseGuardError(null);
        save.mutate({ value: parsed, optimisticLock: data.optimisticLock });
    };
    // Track transition from loading→success to fire toast once.
    var wasSavingRef = useRef(false);
    useEffect(function () {
        var _a, _b, _c, _d;
        if (save.isLoading) {
            wasSavingRef.current = true;
            return;
        }
        if (wasSavingRef.current && !save.isError) {
            wasSavingRef.current = false;
            // CZNA-540 — a save can succeed and still have been let through WITH
            // advisory findings (an unknown MARC tag is a WARNING, not a 400, since
            // TASK-181). Say so, otherwise the 200's `warnings[]` is one more channel
            // that produces a value nobody ever sees.
            var savedWarnings = (_c = (_b = (_a = save.data) === null || _a === void 0 ? void 0 : _a.warnings) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 0;
            (_d = calloutRef.current) === null || _d === void 0 ? void 0 : _d.sendCallout(savedWarnings > 0
                ? {
                    type: 'warning',
                    message: intl.formatMessage({ id: 'ui-czna-authorities.settings.forms.toast.savedWithWarnings' }, { count: savedWarnings }),
                }
                : {
                    type: 'success',
                    message: intl.formatMessage({ id: 'ui-czna-authorities.settings.forms.toast.saved' }),
                });
        }
        if (wasSavingRef.current && save.isError) {
            wasSavingRef.current = false;
        }
    }, [save.isLoading, save.isError, save.data, intl]);
    var wasResettingRef = useRef(false);
    useEffect(function () {
        var _a;
        if (reset.isLoading) {
            wasResettingRef.current = true;
            return;
        }
        if (wasResettingRef.current && !reset.isError) {
            wasResettingRef.current = false;
            (_a = calloutRef.current) === null || _a === void 0 ? void 0 : _a.sendCallout({
                type: 'success',
                message: intl.formatMessage({ id: 'ui-czna-authorities.settings.forms.toast.resetDone' }),
            });
        }
        if (wasResettingRef.current && reset.isError) {
            wasResettingRef.current = false;
        }
    }, [reset.isLoading, reset.isError, intl]);
    var handleReloadAfterConflict = function () {
        save.reset();
        moduleSettingInvalidationKeys(settingKey)
            .forEach(function (queryKey) { return queryClient.invalidateQueries(queryKey); });
        // The data effect will reseed textareaValue from the refetched payload.
    };
    var handleConfirmReset = function () {
        setConfirmResetOpen(false);
        reset.mutate();
    };
    // ─── Render branches ─────────────────────────────────────────────
    if (isLoading) {
        return (React.createElement(Pane, { defaultWidth: "fill", fluidContentWidth: true, paneTitle: React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.paneTitle" }) },
            React.createElement(Loading, null)));
    }
    if (isError && errorReason === 'forbidden') {
        return (React.createElement(Pane, { defaultWidth: "fill", fluidContentWidth: true, paneTitle: React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.paneTitle" }) },
            React.createElement("div", { "data-testid": "settings-editor-admin-only-placeholder" },
                React.createElement(MessageBanner, { type: "warning" },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.placeholder.adminOnly" })))));
    }
    if (isError && errorReason === 'not-found') {
        return (React.createElement(Pane, { defaultWidth: "fill", fluidContentWidth: true, paneTitle: React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.paneTitle" }) },
            React.createElement("div", { "data-testid": "settings-editor-no-config-placeholder" },
                React.createElement(MessageBanner, { type: "warning" },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.placeholder.noConfigYet" })))));
    }
    if (isError) {
        return (React.createElement(Pane, { defaultWidth: "fill", fluidContentWidth: true, paneTitle: React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.paneTitle" }) },
            React.createElement("div", { "data-testid": "settings-editor-generic-error-placeholder" },
                React.createElement(MessageBanner, { type: "error" },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.placeholder.genericError" })))));
    }
    if (!data) {
        return null;
    }
    // ─── Status line ─────────────────────────────────────────────────
    var statusLine = data.customized ? (React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.status.customized", values: {
            when: formatTimestamp(data.updatedAt, intl.locale),
            who: (_c = updatedByName.name) !== null && _c !== void 0 ? _c : (data.updatedBy ? data.updatedBy.slice(0, 8) : '—'),
        } })) : (React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.status.usingDefault", values: { version: formatDefaultVersion(data.defaultVersion) } }));
    // ─── Validation findings panel ──────────────────────────────────
    // Sources (in priority order):
    //   1. Local JSON.parse fail (from useValidateFormConfig or parse-guard)
    //   2. Server-side validate findings (live)
    //   3. PUT 400 errors (after Save attempt)
    //
    // CZNA-540: take findings from ANY live result, not only an invalid one.
    // `valid` is false iff something is BLOCKING, so the old `!valid` gate meant a
    // WARNING-only result (e.g. an unknown MARC tag, non-blocking since TASK-181)
    // rendered nothing at all. Split by severity below so warnings get their own
    // amber section instead of being listed as errors.
    var liveFindings = validate.data ? validate.data.errors : null;
    var saveErrors = save.errorStatus === 400 && save.errorBody && !save.errorBody.valid
        ? save.errorBody.errors
        : null;
    var findingsToShow = saveErrors !== null && saveErrors !== void 0 ? saveErrors : liveFindings;
    var errorsToShow = (_d = findingsToShow === null || findingsToShow === void 0 ? void 0 : findingsToShow.filter(function (e) { return e.severity === 'BLOCKING'; })) !== null && _d !== void 0 ? _d : null;
    var warningsToShow = (_e = findingsToShow === null || findingsToShow === void 0 ? void 0 : findingsToShow.filter(function (e) { return e.severity === 'WARNING'; })) !== null && _e !== void 0 ? _e : null;
    // Save disabled when validation says invalid or local parse failed.
    var isLocallyInvalid = Boolean(validate.localParseError)
        || Boolean(parseGuardError)
        || (validate.data !== null && !validate.data.valid);
    var saveDisabled = save.isLoading || isLocallyInvalid;
    var isConflict = save.errorStatus === 409;
    var resetUnavailable = reset.errorStatus === 409;
    // Reset is disabled when there's no default available (we know that
    // only after a failed reset attempt OR when defaultVersion is null and
    // the value isn't customized) AND while reset is in flight.
    var resetDisabled = reset.isLoading || (data.defaultVersion === null && !data.customized);
    return (React.createElement(Pane, { defaultWidth: "fill", fluidContentWidth: true, paneTitle: React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.paneTitle" }), footer: (React.createElement(PaneFooter
        // Split into renderStart (secondary, left) + renderEnd
        // (primary Save, right) so the buttons span the full width
        // and don't wrap when both lanes are crowded. See sibling
        // FormConfigVisualEditor.tsx for the same pattern.
        , { 
            // Split into renderStart (secondary, left) + renderEnd
            // (primary Save, right) so the buttons span the full width
            // and don't wrap when both lanes are crowded. See sibling
            // FormConfigVisualEditor.tsx for the same pattern.
            renderStart: (React.createElement(React.Fragment, null,
                React.createElement(RawImportExport, { settingKey: settingKey, rawText: textareaValue, onImportedText: function (text) { return setTextareaValue(text); } }),
                React.createElement(Button, { marginBottom0: true, onClick: handleCopy, "data-testid": "settings-editor-copy-btn" },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.buttons.copy" })),
                React.createElement(Button, { marginBottom0: true, onClick: function () { return setConfirmResetOpen(true); }, disabled: resetDisabled, "data-testid": "settings-editor-reset-btn" },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.buttons.reset" })))), renderEnd: (React.createElement(Button, { marginBottom0: true, buttonStyle: "primary", onClick: handleSave, disabled: saveDisabled, "data-testid": "settings-editor-save-btn" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.buttons.save" }))) })) },
        header && (React.createElement("div", { style: { marginBottom: '0.75rem' }, "data-testid": "settings-editor-header-slot" }, header)),
        isConflict && (React.createElement(MessageBanner, { type: "error" },
            React.createElement("div", { "data-testid": "settings-editor-conflict-banner" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.banner.conflict" }),
                ' ',
                React.createElement(Button, { onClick: handleReloadAfterConflict, "data-testid": "settings-editor-conflict-reload-btn" },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.banner.conflictReload" }))))),
        (save.timedOut || reset.timedOut) && (React.createElement(MessageBanner, { type: "error" },
            React.createElement("div", { "data-testid": "settings-editor-timeout-banner" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.error.timeout" })))),
        resetUnavailable && (React.createElement(MessageBanner, { type: "warning" },
            React.createElement("div", { "data-testid": "settings-editor-no-default-placeholder" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.placeholder.noDefaultAvailable" })))),
        React.createElement("div", { style: { marginBottom: '0.5rem', fontSize: '0.875rem' }, "data-testid": "settings-editor-status" }, statusLine),
        React.createElement("div", { style: {
                height: 'calc(100vh - 22rem)',
                minHeight: '30rem',
                display: 'flex',
                flexDirection: 'column',
                marginBottom: '0.5rem',
            } },
            React.createElement(JsonCodeEditor, { value: textareaValue, onChange: function (next) { return setTextareaValue(next); }, testId: "settings-editor-textarea" })),
        validate.isValidating && (React.createElement("div", { style: { fontSize: '0.75rem', color: '#666', marginTop: '0.25rem' }, "data-testid": "settings-editor-validating" },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.validate.inFlight" }))),
        (validate.localParseError || parseGuardError) && (React.createElement(MessageBanner, { type: "error" },
            React.createElement("div", { "data-testid": "settings-editor-parse-error" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.validate.parseError", values: {
                        line: (_g = (_f = validate.localParseError) === null || _f === void 0 ? void 0 : _f.line) !== null && _g !== void 0 ? _g : '?',
                        col: (_j = (_h = validate.localParseError) === null || _h === void 0 ? void 0 : _h.col) !== null && _j !== void 0 ? _j : '?',
                        message: (_m = (_l = (_k = validate.localParseError) === null || _k === void 0 ? void 0 : _k.message) !== null && _l !== void 0 ? _l : parseGuardError) !== null && _m !== void 0 ? _m : '',
                    } })))),
        errorsToShow && errorsToShow.length > 0 && (React.createElement("div", { style: { marginTop: '0.5rem' }, "data-testid": "settings-editor-validation-errors" },
            React.createElement("div", { style: { fontWeight: 'bold', marginBottom: '0.25rem' } },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.validate.errorsHeading" })),
            errorsToShow.map(function (err, idx) { return (React.createElement("div", { 
                // eslint-disable-next-line react/no-array-index-key
                key: "".concat(err.path, "-").concat(err.code, "-").concat(idx), "data-testid": "validation-error-row", style: { fontFamily: 'monospace', fontSize: '0.75rem', marginBottom: '0.125rem' } },
                React.createElement("code", null, err.path),
                ' | ',
                React.createElement("code", null, err.code),
                ' | ',
                err.message)); }))),
        warningsToShow && warningsToShow.length > 0 && (React.createElement("div", { style: { marginTop: '0.5rem' }, "data-testid": "settings-editor-validation-warnings" },
            React.createElement("div", { style: { fontWeight: 'bold', marginBottom: '0.25rem', color: '#a06000' } },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.validate.warningsHeading" })),
            warningsToShow.map(function (warn, idx) { return (React.createElement("div", { 
                // eslint-disable-next-line react/no-array-index-key
                key: "".concat(warn.path, "-").concat(warn.code, "-").concat(idx), "data-testid": "validation-warning-row", style: {
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    marginBottom: '0.125rem',
                    color: '#a06000',
                } },
                React.createElement("code", null, warn.path),
                ' | ',
                React.createElement("code", null, warn.code),
                ' | ',
                warn.message)); }))),
        React.createElement(ConfirmationModal, { open: confirmResetOpen, heading: React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.confirmReset.heading" }), message: React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.confirmReset.message" }), confirmLabel: React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.confirmReset.confirm" }), cancelLabel: React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.confirmReset.cancel" }), onConfirm: handleConfirmReset, onCancel: function () { return setConfirmResetOpen(false); } }),
        React.createElement(Callout, { ref: calloutRef })));
};
export default ModuleSettingEditor;
